import React, { useCallback, useContext, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useMutation, useQuery } from '@apollo/client'

import { CHG } from 'tools/Input'
import {
  GenerateNav,
  RedirectDefault,
  defaultNavPath
} from 'tools/Nav/SectionRouter'
import notifier from 'tools/Notify'
import { Nothing } from 'utils/react'

import { GlobalContext } from 'reducer/global'
import { usePage } from 'reducer/global/hooks'

export const defaultFrame = {
  Context: undefined, // <Context> func [required] —
  Provider: undefined, // <Provider> func [required] -
  // Nav/Section Info
  routes: {}, // dictionary of routes (like SectionRouter)
  root: '',
  section: '', // section name
  navPath: defaultNavPath,
  navClass: '',
  tabClass: '',

  // Known Components
  Default: undefined, // <Component> [optional] — the component used when no target is selected
  Show: undefined, // <Component> [optional]
  Create: undefined, // <Create> [optional]

  // Hook component can contain top level actions for section such as useEffect
  Hook: Nothing,

  // REDUCER ACTIONS
  actions: {
    LOAD: undefined, // flag [required] — reducer flag to use for setting
    RESET: undefined, // flag [required] - reducer flag for resetting state
    DELETE: undefined, // flag — reducer flag for deleting from state
    SAVE: undefined, // flag — reducer for updating
    MERGE: undefined // flag [required] — reducer flag for merging changes
  },
  upsert: {
    mutation: undefined,
    queryName: '', // name of the query in results
    refetchQueries: undefined // [list], if not defined defaults to the load queryName
  },
  load: {
    query: undefined,
    fetchPolicy: '', // additonal args for upsert
    queryName: '', // name of the query in results
    component: 'results',
    list: true
  },
  targetKey: 'targetId', // from the router
  tabKey: 'view', // from the router
  debug: false // 'name' // if truthy, this is the label for debugging
}

export function makeFrame(frame) {
  frame = { ...defaultFrame, ...frame }
  if (!frame.load.fetchPolicy) {
    frame.load.fetchPolicy = 'cache-and-network'
  }
  return frame
}

////////////////////////////////////////////////////////////////////////////////
export default function SectionEditor({ frame }) {
  const { Provider, Hook } = frame

  return (
    <Provider>
      <Hook root={frame.root} />
      <Navigator frame={frame} />
    </Provider>
  )
}

////////////////////////////////////////////////////////////////////////////////
export function Navigator({ frame }) {
  const {
    targetKey,
    tabKey,
    Default,
    name,
    routes,
    section,
    root,
    navPath,
    navClass,
    tabClass
  } = frame

  const params = useParams()
  const targetId = params[targetKey]
  const view = params[tabKey]

  let target = targetId !== '_' ? targetId : undefined

  let showNav = false
  let innerNav = undefined
  if (target) {
    showNav = true
    innerNav = {
      Component: GenerateNav,
      args: { routes, navPath, root, navClass, tabClass, params }
    }
  }

  usePage({ section, innerNav }, [showNav, section, target])

  debug('Navigator', frame, { name, target, targetId, view, frame })

  // useEffect(() => {
  //   if (!target && frame.noTarget) {
  //     const newTarget = frame.noTarget(params, state, glob)
  //     if (newTarget) {
  //       history.replace(newTarget)
  //     }
  //   }
  // }, [frame, params, state, glob, target, history, view])

  // useEffect(() => {
  //   if (actions.RESET && id && target !== id && target !== pathId) {
  //     dispatch({ type: actions.RESET })
  //   }
  // }, [dispatch, target, id, pathId, actions.RESET])

  //// future todo: If we want a scenario with no targetId at all we can add
  //// more logic here

  // if we have no target, then we should be in "Default" mode
  if (!target) {
    if (Default) {
      return (
        <Editor frame={frame} targetId={target}>
          <Default />
        </Editor>
      )
    } else {
      return <RedirectDefaultFirst routes={routes} root={root} targetId={'_'} />
    }
  }

  const { Component } = routes[view] ?? {}

  return (
    <Loader targetId={target} frame={frame}>
      {Component ? (
        <Editor frame={frame} targetId={target}>
          <Component />
        </Editor>
      ) : (
        <div className="flex-center mt5">
          rerouting
          <RedirectDefaultFirst routes={routes} root={root} targetId={target} />
        </div>
      )}
    </Loader>
  )
}

function RedirectDefaultFirst({ routes, root, targetId }) {
  const key0 = Object.keys(routes)[0]

  // todo: switch to navPath; and/or have flag for "no target"
  return <RedirectDefault path={`${root}/${targetId}/${key0}`} />
}
////////////////////////////////////////////////////////////////////////////////
export function Loader({
  targetId = undefined,
  frame,
  children,
  variables: vars = {}
}) {
  const { load, Context, actions } = frame
  const { query, queryName, fetchPolicy } = load
  const [, notify] = useContext(notifier.Context)
  const [state, dispatch] = useContext(Context)
  const [{ user }] = useContext(GlobalContext)
  const skip = !targetId
  const variables = { id: targetId, ...vars, ...(state._LoaderVars || {}) }
  debug('Loader', frame, { skip, variables, targetId, frame })
  useQuery(query, {
    variables,
    query,
    fetchPolicy,
    onCompleted(result) {
      debug('Loader onCompleted', frame, { result, targetId, variables, frame })
      if (result) {
        result = result[queryName]
        if (!result.success) {
          notifier.error(notify, result.reason || 'Unexpected result')
        } else {
          doDispatch(dispatch, actions.LOAD, { user }, result, load)
        }
      }
    },
    onError(error) {
      console.error('<Loader>', error)
    }
  })

  return children
}

////////////////////////////////////////////////////////////////////////////////
export function Editor({
  frame,
  create = false,
  children,
  targetId = undefined
}) {
  const { Context, upsert } = frame
  const [state, dispatch] = useContext(Context)
  const [{ user }] = useContext(GlobalContext)
  // const params = useParams()
  // const history = useHistory()
  const [mutationFunc] = useMutation(upsert.mutation)
  const id = state.id
  const onSave = useCallback(
    (
      {
        token = undefined,
        orig = undefined,
        dirty = true,
        makeVars = undefined,
        component = undefined,
        ...args
      },
      value,
      good,
      bad
    ) => {
      const { actions } = frame
      debug('Editor onSave', frame, {
        orig,
        component,
        value,
        dirty,
        token
      })
      if (!dirty) {
        good && good()
      } else if (orig !== value && dirty) {
        let variables
        if (makeVars) {
          variables = makeVars({ component, token, value, frame, args })
        } else if (component) {
          variables = { [component]: { id, [token]: value } }
        } else {
          variables = { id, [token]: value }
        }
        const { queryName, refetchQueries } = frame.upsert
        debug('Editor SAVE', frame, { component, variables, frame, queryName })
        mutationFunc({
          variables,
          update(cache, { data }) {
            const result = data[queryName]
            debug('Editor RESULT', frame, { data, result, frame })
            if (result?.success !== true) {
              bad &&
                bad(result?.reason || 'unexpected query result for ' + queryName)
              return
            }

            let value = result.result
            if (value === undefined && component && result[component]) {
              value = result[component]
            }

            doDispatch(
              dispatch,
              result.deleted ? actions.DELETE : actions.SAVE || actions.LOAD,
              { user, component },
              value,
              { ...frame.upsert, ...component }
            )

            // const paramId = params[frame.targetKey]
            // if (pathKey !== false && paramId !== value[pathKey]) {
            //   if (frame.tabEdit) {
            //     history.replace(`${basePath}/${value[pathKey]}/${frame.tabEdit}`)
            //   } else if (frame.navPath) {
            //     history.replace(
            //       frame.navPath(
            //         {
            //           view: params[frame.tabKey],
            //           frame,
            //           params: { ...params, targetId: value[pathKey] }
            //         },
            //         value,
            //         basePath
            //       )
            //     )
            //   } else {
            //     history.replace(
            //       `${basePath}/${value[pathKey]}/${params[frame.tabKey]}`
            //     )
            //   }
            // }
            good && good(CHG.SAVED, value)
          },
          refetchQueries,
          onQueryUpdated(observable) {
            return observable.refetch()
          }
        })
      }
    },
    [dispatch, mutationFunc, id, frame, user]
  )
  useEffect(() => {
    dispatch({ type: frame.actions.MERGE, value: { onSave } })
  }, [onSave, dispatch, frame])

  return children
}

////////////////////////////////////////////////////////////////////////////////
function doDispatch(dispatch, type, vars, value, { component, list }) {
  if (type === undefined) {
    console.log('NO HANDLER FOR DISPATCH TYPE!', { vars, value, component })
  } else {
    if (component) {
      value = value[component]
    }
    if (list) {
      value = value[0]
    }
    value = { ...value, _loaded: true }
    dispatch({ type, ...vars, component, value })
  }
}

function debug(component, { debug }, data) {
  if (debug) {
    console.log(`${debug} <${component}>`, data)
  }
}
